/* global PiniaSetupStoreHelpers */
import { nextTick } from 'vue';
import { useHotkey } from 'vuetify';
import { defineStore } from 'pinia';

import { KHotkeyError } from '@/helpers/throwables';

type UseHotkeyParams = Parameters<typeof useHotkey>;

function hotkeysStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const _cleanups: Record<string, (() => void) | undefined> = {};
  const _callbacks: Record<string, ((e: KeyboardEvent) => void) | undefined> =
    {};

  function add(
    keys: string,
    callback: UseHotkeyParams[1],
    options: UseHotkeyParams[2],
    comment?: string, // TODO command palette
  ) {
    // if components with the same hotkey's init are toggled with `v-if` then `hotkeysStore.add` must always run later (nextTick), it needs for `v-window-item` unmounting components
    void nextTick(() => {
      if (_cleanups[keys]) {
        throw new KHotkeyError(`The hotkey '${keys}' is already set`);
      }
      const cleanup = useHotkey(keys, callback, options);
      _cleanups[keys] = cleanup;
      _callbacks[keys] = callback;
    });
  }

  // we need to remove hotkeys manually and keep the list of available hotkeys because eventually we will need that list at the moment
  function remove(keys: string) {
    if (!_cleanups[keys]) return;
    _cleanups[keys]();
    /* eslint-disable-next-line @typescript-eslint/no-dynamic-delete */
    delete _cleanups[keys];
    /* eslint-disable-next-line @typescript-eslint/no-dynamic-delete */
    delete _callbacks[keys];
  }

  function getCallback(keys: string) {
    return _callbacks[keys];
  }

  return {
    add,
    remove,
    getCallback,
  };
}

export const useHotkeysStore = defineStore('hotkeysStore', hotkeysStoreSetup);
